import type { SyncDeliveryErrorCode } from '../sync/sync-delivery-errors.js';
import type { HoldingUnitEventEntityType } from '../holding-units/holding-unit-event-envelope.dto.js';
import type { BillingStatus, CompanyStatus } from '../tenancy/company.dto.js';
import type { AuthErrorCode } from './auth-error-codes.js';

export const authSessionTypeValues = ['identity', 'company'] as const;

export type AuthSessionType = (typeof authSessionTypeValues)[number];

export const authPrincipalTypeValues = ['user', 'station'] as const;
export type AuthPrincipalType = (typeof authPrincipalTypeValues)[number];

export const authSessionPurposeValues = ['interactive', 'station_sync'] as const;
export type AuthSessionPurpose = (typeof authSessionPurposeValues)[number];

interface BaseAuthSessionClaimsDto {
  sessionType: AuthSessionType;
  sessionId: string;
  principalType: AuthPrincipalType;
  principalId: string;
  purpose: AuthSessionPurpose;
  companyId?: string;
  authzVersion: number;
}

export interface UserAuthSessionClaimsDto extends BaseAuthSessionClaimsDto {
  principalType: 'user';
  principalId: string;
  purpose: 'interactive';
  userId: string;
}

export interface StationAuthSessionClaimsDto extends BaseAuthSessionClaimsDto {
  principalType: 'station';
  purpose: 'station_sync';
  sessionType: 'company';
  companyId: string;
  stationId: string;
  deviceId: string;
  deviceClaimId: string;
  permissionProfileVersion: number;
}

export type AuthSessionClaimsDto = UserAuthSessionClaimsDto | StationAuthSessionClaimsDto;

interface BaseAuthTokensResponse {
  accessToken: string;
  refreshToken: string;
  expiresInMs: number;
  sessionId: string;
  principalType: AuthPrincipalType;
  principalId: string;
  purpose: AuthSessionPurpose;
  sessionType: AuthSessionType;
  companyId: string | null;
  authzVersion: number;
}

export interface UserAuthTokensResponse extends BaseAuthTokensResponse {
  principalType: 'user';
  purpose: 'interactive';
  userId: string;
}

export interface StationAuthTokensResponse extends BaseAuthTokensResponse {
  principalType: 'station';
  purpose: 'station_sync';
  sessionType: 'company';
  companyId: string;
  stationId: string;
  deviceId: string;
  deviceClaimId: string;
  permissionProfileVersion: number;
}

export type AuthTokensResponse = UserAuthTokensResponse | StationAuthTokensResponse;

export interface StationSyncSessionRequest {
  stationId: string;
  deviceId: string;
  deviceClaimId: string;
}

export type StationSyncSessionResponse = StationAuthTokensResponse;

export const stationSyncRecheckKindValues = [
  'station_binding',
  'permission_profile',
  'feature_entitlement',
  'company_operability',
  'contract_support',
  'actor_membership',
  'holding_unit_stream_heads',
] as const;
export type StationSyncRecheckKind = (typeof stationSyncRecheckKindValues)[number];

export interface StationSyncPreflightRequest {
  recheckKinds?: StationSyncRecheckKind[];
  actorUserIds?: string[];
  routeChecks?: StationSyncRouteProbe[];
  requiredFeatureKeys?: string[];
  contractChecks?: StationSyncContractProbe[];
  holdingUnitStreams?: StationSyncHoldingUnitStreamProbe[];
}

export interface StationSyncRouteProbe {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  path: string;
}

export interface StationSyncContractProbe {
  eventType: string;
  schemaVersion: number;
}

export interface StationSyncHoldingUnitStreamProbe {
  entityType: HoldingUnitEventEntityType;
  entityId: string;
  expectedPredecessorVersion: number;
}

export interface StationSyncPreflightEvidence {
  stationBindingCurrent: boolean;
  permissionProfileCurrent: boolean;
  routes: Array<
    StationSyncRouteProbe & {
      allowed: boolean;
      purpose: 'system' | 'background' | 'operator' | null;
    }
  >;
  features: Array<{ featureKey: string; enabled: boolean }>;
  companyOperability: {
    companyStatus: CompanyStatus | null;
    planCode: string | null;
    billingStatus: BillingStatus | null;
    planActive: boolean;
    trialExpiresAt: string | null;
    operable: boolean;
  };
  contracts: Array<StationSyncContractProbe & { supported: boolean }>;
  actors: Array<{ userId: string; hasHistoricalMembership: boolean }>;
  holdingUnitStreamHeads: Array<
    StationSyncHoldingUnitStreamProbe & {
      cloudHeadVersion: number;
      cloudLastEventId: string | null;
      replayReady: boolean;
    }
  >;
}

export interface StationSyncPreflightIssue {
  recheckKind: StationSyncRecheckKind;
  errorCode: AuthErrorCode | SyncDeliveryErrorCode;
  message: string;
}

export interface StationSyncPreflightResponse {
  status: 'ready' | 'blocked';
  checkedAt: string;
  companyId: string;
  stationId: string;
  deviceId: string;
  deviceClaimId: string;
  permissionProfileVersion: number;
  issues: StationSyncPreflightIssue[];
  evidence: StationSyncPreflightEvidence;
}

export interface RevokeStationSyncSessionRequest {
  reason: string;
}

export interface RevokeStationSyncSessionResponse {
  sessionId: string;
  revokedAt: string;
  reason: string;
}

export interface AuthSessionMetadataDto {
  sessionId: string;
  principalType: AuthPrincipalType;
  principalId: string;
  purpose: AuthSessionPurpose;
  sessionType: AuthSessionType;
  companyId: string | null;
  authzVersion: number;
}
